// ============================================================
// Personal-email domain classifier.
// ============================================================
// See docs/users-onboarding-billing-plan.md §1.3.
//
// The tenancy fork treats corporate-email vs personal-email signups
// differently:
//
//   - Corporate → offered "Just me / For my company" fork; may claim
//     the domain (Team workspace); paid tiers permitted.
//   - Personal → auto-Personal workspace; cannot claim any domain;
//     blocked from Growth/Enterprise upgrade (see business-model.md §1
//     rationale: individual-owned data becomes a sovereignty landmine
//     when it holds a company's compliance posture).
//
// This module is the single source of truth for that classification.
// Keep the blocklist conservative — false-positive (calling a
// corporate domain personal) blocks a legitimate upgrade; false-negative
// (calling a personal domain corporate) opens the sovereignty hole. On
// tie, prefer the false-positive: worst case the user complains and
// support flips them.
//
// Kept in packages/core (no platform deps) so the same classifier runs
// on the Worker (signup path) and in the SPA (client-side UX hints).
/**
 * The blocklist. Lowercased, exact match on the *domain* portion of
 * the email. Includes:
 *
 *   - Global consumer providers (gmail, outlook, yahoo, icloud, aol,
 *     hotmail, live, proton, gmx, mail.com, tutanota, fastmail,
 *     zoho.mail, hey, pm.me).
 *   - Aliases / country ccTLD variants (yahoo.co.uk, hotmail.co.uk,
 *     outlook.jp, etc.) — extend as customer support learns new ones.
 *   - Disposable / throwaway (mailinator, guerrillamail, 10minutemail,
 *     tempmail, yopmail, sharklasers) — these should NEVER hold a paid
 *     workspace; blocking them at the tenancy layer stops abuse before
 *     billing sees it.
 *
 * NOT included: catch-all corporate mail hosts (fastmail hosts many
 * custom domains; the *domain the user's email sits on* is what we
 * check, not the mail provider — so a user at `steve@my-consultancy.com`
 * is corporate even if the MX record points to Fastmail).
 */
const PERSONAL_EMAIL_DOMAINS = new Set([
    // Global consumer providers
    'gmail.com',
    'googlemail.com',
    'yahoo.com',
    'yahoo.co.uk',
    'yahoo.co.jp',
    'yahoo.fr',
    'yahoo.de',
    'yahoo.es',
    'yahoo.ca',
    'ymail.com',
    'rocketmail.com',
    'outlook.com',
    'outlook.co.uk',
    'outlook.jp',
    'hotmail.com',
    'hotmail.co.uk',
    'hotmail.fr',
    'hotmail.de',
    'hotmail.es',
    'live.com',
    'live.co.uk',
    'msn.com',
    'icloud.com',
    'me.com',
    'mac.com',
    'aol.com',
    'proton.me',
    'protonmail.com',
    'pm.me',
    'gmx.com',
    'gmx.net',
    'gmx.de',
    'gmx.us',
    'mail.com',
    'mail.ru',
    'yandex.com',
    'yandex.ru',
    'zoho.com',
    'tutanota.com',
    'tuta.io',
    'fastmail.com',
    'fastmail.fm',
    'hey.com',
    'qq.com',
    '163.com',
    '126.com',
    'sina.com',
    'naver.com',
    'daum.net',
    'hanmail.net',
    // Disposable / throwaway
    'mailinator.com',
    'guerrillamail.com',
    '10minutemail.com',
    'tempmail.com',
    'temp-mail.org',
    'yopmail.com',
    'sharklasers.com',
    'throwawaymail.com',
    'trashmail.com',
    'maildrop.cc',
    'getnada.com',
    'dispostable.com',
]);
/**
 * Extract the lowercased domain from an email address.
 * Returns null for anything that doesn't look like `<local>@<domain>`.
 * Deliberately permissive on the local part — we're only classifying,
 * not RFC-5322 validating.
 */
export function extractEmailDomain(email) {
    if (!email || typeof email !== 'string')
        return null;
    // Exactly one `@` — reject "two@at@signs.com" and similar. RFC allows
    // quoted local parts with an `@`, but WorkOS-issued emails never look
    // like that, and accepting them would let a personal-domain user
    // smuggle a corporate-looking address through the classifier.
    const atCount = (email.match(/@/g) ?? []).length;
    if (atCount !== 1)
        return null;
    const at = email.indexOf('@');
    if (at <= 0 || at === email.length - 1)
        return null;
    const domain = email.slice(at + 1).trim().toLowerCase();
    if (!domain || !domain.includes('.'))
        return null;
    return domain;
}
/**
 * Is this a personal-email domain? Returns false for anything not in
 * the blocklist (including unrecognised country ccTLDs — we prefer
 * false-negative-then-support-flip over blocking a legitimate corp).
 */
export function isPersonalEmailDomain(domain) {
    return PERSONAL_EMAIL_DOMAINS.has(domain.trim().toLowerCase());
}
export function classifyEmail(email) {
    const domain = extractEmailDomain(email);
    if (!domain)
        return 'invalid';
    return isPersonalEmailDomain(domain) ? 'personal' : 'corporate';
}
/**
 * The maintained blocklist, exposed for tests and for UI features that
 * want to render "we recognise <count> personal domains" copy. Return
 * a copy so callers can't mutate the shared set.
 */
export function personalEmailDomains() {
    return new Set(PERSONAL_EMAIL_DOMAINS);
}
//# sourceMappingURL=email-domain.js.map